import { signOut } from '#auth'
import { useAuth } from '#imports'
import { nextTick } from 'vue'
import { isPublicRoute, getSafeRedirectUrl } from '~/utils/auth'
import { useErrorPopup } from '../composables/useErrorPopup'

export default defineNuxtRouteMiddleware(async (to, from) => {
  const { status, user, session } = useAuth()
  const { useError } = useErrorPopup()
  
  // Skip middleware execution during server-side rendering for localStorage operations
  if (!process.client && (to.path === '/setup-wizard' || to.path === '/')) {
    return;
  }
  
  // If coming from accept-terms, force a session refresh to get updated user data
  if (from?.path === '/accept-terms') {
    try {
      // Give more time for the session to update properly
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // Re-fetch the auth composable to get fresh data
      const freshAuth = useAuth()
    } catch (error) {
      console.error('Failed to refresh session:', error)
    }
  }

  const userName = user.value?.name || session.value?.user?.name
  const termsAccepted = user.value?.termsAccepted || session.value?.user?.termsAccepted
  const setupDone = user.value?.setupDone || session.value?.user?.setupDone
  const hasPlan = user.value?.plan && user.value.plan !== "none"

  // Check for banned user
  if (user.value?.banned) {
    useError.showError('Cannot login, this account is banned')
    signOut()
  }

  if (!user) {
    signOut()
    return navigateTo('/')
  }

  // Track successful authentication
  const isNewAuthentication = process.client &&
    status.value === 'authenticated' &&
    user.value &&
    !sessionStorage.getItem('authSuccessTracked')
    
  if (isNewAuthentication) {
    sessionStorage.setItem('authSuccessTracked', 'true')
    const { trackAuthSuccess } = useAnalytics()
    trackAuthSuccess()
  }
  // Handle unauthenticated users trying to access protected routes
  if (isPublicRoute(to.path) && status.value !== 'authenticated') {
    return
  }
  
  if (!isPublicRoute(to.path)) {
    if (status.value !== 'authenticated') {
      return navigateTo('/');
    }
    if (to.path.includes('admin') && user.value?.role !== 'admin') {
      return navigateTo('/');
    }
  }

  // If user is not authenticated, allow access to public routes
  if (status.value !== 'authenticated' && isPublicRoute(to.path)) {
    return
  }

  // Special handling for home page ("/") - authenticated users still need setup/plan checks
  // Other public routes (like /logout, /about, etc.) can be accessed freely by authenticated users
  if (isPublicRoute(to.path) && to.path !== '/') {
    return
  }

  // From here on, we're dealing with authenticated users accessing protected routes
  // Now we need to enforce the setup/plan checks

  if (!userName) {
    if (to.path === '/register') return
    return navigateTo({
      path: '/register',
      query: {
        email: user.value?.email || session.value?.user?.email,
      }
    }, { redirectCode: 302 })
  }

  if (!termsAccepted) {
    if (to.path === '/accept-terms') return;
    return navigateTo('/accept-terms', { redirectCode: 302 })
  }
  
  if (!setupDone) {
    // Check if we're on setup-wizard page already - if so, allow it
    if (to.path === '/setup-wizard') {
      return;
    }
    
    // Redirect to setup-wizard (it will handle guest budget transfer logic)
    return navigateTo('/setup-wizard', { redirectCode: 302 });
  }
  
  if (!hasPlan) {
    if (to.path === '/choose-plan') {
      return;
    }
    return navigateTo('/choose-plan')
  }

  return;
})
